import React, { useState, useEffect } from "react";
import "../css/customers.css";
import { toast } from "react-toastify";
import rcsLogo from "../assets/logo.png";

import * as XLSX from "xlsx";
import { saveAs } from "file-saver";


// ✅ Normalize API base (same as Dashboard, UserManagement, etc.)
const RAW_API_BASE =
  (typeof import.meta !== "undefined" ? import.meta.env?.VITE_API_BASE : undefined) ||
  process.env.REACT_APP_API_BASE ||
  "https://pos-backend-58o9.onrender.com";

const API_BASE = RAW_API_BASE.replace(/\/+$/, "").replace(/\/api$/i, "");
const apiPath = (path) => `${API_BASE}${path.startsWith("/api") ? path : `/api${path}`}`;

// ✅ Auth helpers (optional if backend uses protected routes)
const getAuth = () => {
  const raw = localStorage.getItem("rcs_auth") || sessionStorage.getItem("rcs_auth");
  try { return raw ? JSON.parse(raw) : null; } catch { return null; }
};
const authHeader = () => {
  const s = getAuth();
  return s?.access_token ? { Authorization: `Bearer ${s.access_token}` } : {};
};

const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newCustomer, setNewCustomer] = useState({
    name: "",
    phone: "",
    countryCode: "+592", // Default to Guyana
    address: "",
    status: "active",
  });
  const [nextCustomerId, setNextCustomerId] = useState(null);


const [currentPage, setCurrentPage] =
  useState(1);

const ROWS_PER_PAGE = 5;


const [processing, setProcessing] =
  useState(false);

const [processStatus, setProcessStatus] =
  useState(null); // success | error

const [processMessage, setProcessMessage] =
  useState("");


  // ✅ Load customers
  useEffect(() => {
    fetch(apiPath("/customers"), { headers: { ...authHeader() } })
      .then((res) => res.json())
      .then((data) => setCustomers(data))
      .catch((err) => console.error("Failed to load customers", err));
  }, []);

const filtered = customers.filter((c) => {

  const term = searchTerm.trim().toLowerCase();

  const customerNumber =
    `RCS${String(c.id).padStart(4, "0")}`.toLowerCase();

  return (

    customerNumber.includes(term) ||

    c.name?.toLowerCase().includes(term) ||

    c.phone?.toLowerCase().includes(term) ||

    c.address?.toLowerCase().includes(term) ||

    c.customer_type?.toLowerCase().includes(term)

  );

});

const totalPages = Math.ceil(
  filtered.length / ROWS_PER_PAGE
);

const paginatedCustomers =
  filtered.slice(
    (currentPage - 1) *
      ROWS_PER_PAGE,
    currentPage *
      ROWS_PER_PAGE
  );

  useEffect(() => {
  setCurrentPage(1);
}, [searchTerm]);

  useEffect(() => {
    if (customers.length > 0) {
      const maxId = Math.max(...customers.map((c) => c.id));
      setNextCustomerId(maxId + 1);
    } else {
      setNextCustomerId(1);
    }
  }, [customers]);

const handleAddCustomer = async () => {

  if (!newCustomer.name) {
    toast.warn("Name is required.");
    return;
  }

  setProcessing(true);

  setProcessStatus(null);

  setProcessMessage(
    "Creating customer profile..."
  );

  const cleanedPhone =
    newCustomer.phone.trim() === ""
      ? null
      : `${newCustomer.countryCode}${newCustomer.phone.trim()}`;

  const customerToAdd = {
    ...newCustomer,
    phone: cleanedPhone,
  };

  try {

    // 5 SECOND LOADER
    await new Promise((resolve) =>
      setTimeout(resolve, 5000)
    );

    const res = await fetch(
      apiPath("/customers"),
      {
        method: "POST",

        headers: {
          "Content-Type":
            "application/json",
          ...authHeader(),
        },

        body: JSON.stringify(
          customerToAdd
        ),
      }
    );

    if (!res.ok)
      throw new Error(
        "Failed to create customer"
      );

    const created =
      await res.json();

    setCustomers([
      ...customers,
      created,
    ]);

    setNewCustomer({
      name: "",
      phone: "",
      address: "",
      status: "active",
      countryCode: "+592",
    });

    setProcessStatus("success");

    setProcessMessage(
      "Customer added successfully!"
    );

    setTimeout(() => {

      setProcessing(false);

      setShowAddModal(false);

    }, 2500);

  } catch (err) {

    console.error(
      "Add failed",
      err
    );

    setProcessStatus("error");

    setProcessMessage(
      "Failed to add customer."
    );

    setTimeout(() => {

      setProcessing(false);

    }, 2500);
  }
};
  const handleRowClick = (customer) => {
    const match = customer.phone?.match(/^(\+\d{1,4})(.*)/);
    const countryCode = match ? match[1] : "+592";
    const numberOnly = match ? match[2] : "";

    setSelectedCustomer({
      ...customer,
      countryCode,
      phone: numberOnly,
    });
  };

  const handleChange = (field, value) => {
    setSelectedCustomer({ ...selectedCustomer, [field]: value });
  };

const handleSave = async () => {

  setProcessing(true);

  setProcessStatus(null);

  setProcessMessage(
    "Updating customer..."
  );

  const cleanedPhone =
    selectedCustomer.phone?.trim() === ""
      ? null
      : `${
          selectedCustomer.countryCode ||
          "+592"
        }${selectedCustomer.phone.trim()}`;

  const customerToUpdate = {
    ...selectedCustomer,
    phone: cleanedPhone,
  };

  try {

    // 5 SECOND LOADER
    await new Promise((resolve) =>
      setTimeout(resolve, 5000)
    );

    const res = await fetch(
      apiPath(
        `/customers/${selectedCustomer.id}`
      ),
      {
        method: "PUT",

        headers: {
          "Content-Type":
            "application/json",
          ...authHeader(),
        },

        body: JSON.stringify(
          customerToUpdate
        ),
      }
    );

    if (!res.ok)
      throw new Error(
        "Failed to update"
      );

    const updated =
      await res.json();

    setCustomers(
      customers.map((c) =>
        c.id === updated.id
          ? updated
          : c
      )
    );

    setProcessStatus("success");

    setProcessMessage(
      "Customer updated successfully!"
    );

    setTimeout(() => {

      setProcessing(false);

      setSelectedCustomer(null);

    }, 2500);

  } catch (err) {

    console.error(
      "Save failed",
      err
    );

    setProcessStatus("error");

    setProcessMessage(
      "Failed to save changes."
    );

    setTimeout(() => {

      setProcessing(false);

    }, 2500);
  }
};


  // ✅ Toggle customer active/inactive
  const toggleStatus = async (id, currentStatus) => {
    const newStatus = currentStatus === "active" ? "inactive" : "active";
    try {
      const res = await fetch(apiPath(`/customers/${id}/status`), {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...authHeader() },
        body: JSON.stringify({ status: newStatus }),
      });
      const updated = await res.json();
      setCustomers(customers.map((c) => (c.id === updated.id ? updated : c)));
      toast.info("Customer status updated.");
    } catch (err) {
      console.error("Failed to toggle status", err);
      toast.error("Failed to update status.");
    }
  };

  // ✅ Delete customer
  const handleDeleteCustomer = async (id) => {
    if (!window.confirm("Are you sure you want to delete this customer?")) return;
    try {
      const res = await fetch(apiPath(`/customers/${id}`), {
        method: "DELETE",
        headers: { ...authHeader() },
      });

      if (!res.ok) throw new Error("Delete failed");

      setCustomers(customers.filter((c) => c.id !== id));
      toast.success("Customer deleted.");
    } catch (err) {
      console.error("Delete failed", err);
      toast.error("Failed to delete customer.");
    }
  };




  const handleExportCustomers = () => {

  const exportData = filtered.map((customer) => ({

    "Customer No":
      `RCS${String(customer.id).padStart(4, "0")}`,

    Name:
      customer.name || "",

    "Phone Number":
      customer.phone || "",

    Address:
      customer.address || "",

    Type:
      customer.customer_type
        ? customer.customer_type.charAt(0).toUpperCase() +
          customer.customer_type.slice(1)
        : "",

    Status:
      customer.status
        ? customer.status.charAt(0).toUpperCase() +
          customer.status.slice(1)
        : "",

  }));

  const worksheet =
    XLSX.utils.json_to_sheet(exportData);

  const workbook =
    XLSX.utils.book_new();

  XLSX.utils.book_append_sheet(
    workbook,
    worksheet,
    "Customers"
  );

  worksheet["!cols"] = [

    { wch: 18 }, // Customer No
    { wch: 35 }, // Name
    { wch: 22 }, // Phone
    { wch: 40 }, // Address
    { wch: 18 }, // Type
    { wch: 15 }, // Status

  ];

  const excelBuffer =
    XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

  const blob = new Blob(
    [excelBuffer],
    {
      type:
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
    }
  );

  saveAs(
    blob,
    `Customers_${new Date()
      .toISOString()
      .slice(0, 10)}.xlsx`
  );

  toast.success(
    "Customer list exported successfully."
  );

};




return (
  <div className="modern-customers-page">
{/* =====================================================
    HERO SECTION
===================================================== */}
<div className="modern-customers-hero">

  <div className="modern-customers-hero-top">

    <div className="modern-customers-pill">
      <span>Customer Intelligence</span>
    </div>

<div
  style={{
    display: "flex",
    gap: "12px",
  }}
>

  <button
    className="modern-export-btn"
    onClick={handleExportCustomers}
  >
    Export
  </button>

  <button
    className="modern-add-customer-btn"
    onClick={() =>
      setShowAddModal(true)
    }
  >
    + New Customer
  </button>

</div>

  </div>

  <div className="modern-customers-hero-grid">

    {/* LEFT */}
    <div className="modern-customers-hero-left">

      <h1>
        Customer
        <br />
        Overview
      </h1>

      <p>
        Monitor customer growth,
        loyalty engagement and
        relationship activity across
        the RCS POS ecosystem.
      </p>

    </div>

    {/* RIGHT */}
    <div className="modern-customers-analytics">

      {/* CARD 1 */}
      <div className="modern-customer-stat-card">

        <span className="modern-stat-label">
          Total Customers
        </span>

        <h2>
          {customers.length}
        </h2>

      </div>

      {/* CARD 2 */}
      <div className="modern-customer-stat-card">

        <span className="modern-stat-label">
          Active
        </span>

        <h2>
          {
            customers.filter(
              (c) =>
                c.status ===
                "active"
            ).length
          }
        </h2>

      </div>

      {/* CARD 3 */}
      <div className="modern-customer-stat-card">

        <span className="modern-stat-label">
          Inactive
        </span>

        <h2>
          {
            customers.filter(
              (c) =>
                c.status ===
                "inactive"
            ).length
          }
        </h2>

      </div>

      {/* MINI GRAPH */}
      <div className="modern-customer-graph-card">

        <div className="modern-graph-header">

          <h4>
            New Customers
          </h4>

          <span>
            Last 7 Days
          </span>

        </div>

        <div className="modern-graph-bars">

          {[40, 65, 30, 85, 55, 72, 95].map(
            (h, i) => (
              <div
                key={i}
                className="modern-bar-wrap"
              >

                <div
                  className="modern-bar"
                  style={{
                    height: `${h}%`,
                  }}
                />

              </div>
            )
          )}

        </div>

      </div>

    </div>

  </div>

</div>

    {/* =====================================================
        SEARCH
    ===================================================== */}
    <div className="modern-customer-search-wrap">

      <input
        type="text"
        placeholder="Search customer, phone, address or type..."
        value={searchTerm}
        onChange={(e) =>
          setSearchTerm(
            e.target.value
          )
        }
        className="modern-customer-search"
      />

    </div>

    {/* =====================================================
        TABLE CARD
    ===================================================== */}
    <div className="modern-customers-card">

      <div className="modern-customers-card-header">

        <h3>
          Customer Directory
        </h3>

        <span>
          {filtered.length}
          {" "}
          Customers
        </span>

      </div>

      <div className="modern-customers-table-wrap">

        <table className="modern-customers-table">

          <thead>

            <tr>

              <th>
                Customer No.
              </th>

              <th>
                Name
              </th>

              <th>
                Phone
              </th>

              <th>
                Address
              </th>

              <th>
                Status
              </th>

              <th>
                Type
              </th>

              <th>
                Actions
              </th>

            </tr>

          </thead>

          <tbody>

            {filtered.length === 0 ? (

              <tr>

                <td
                  colSpan="7"
                  className="modern-empty-customers"
                >

                  <div className="modern-empty-customers-inner">

                    <h4>
                      No Customers Found
                    </h4>

                    <p>
                      Try adjusting your
                      search or add a new
                      customer.
                    </p>

                  </div>

                </td>

              </tr>

            ) : (

              paginatedCustomers.map((c) => (

                <tr key={c.id}>

                  <td className="modern-customer-id">
                    {`RCS${String(
                      c.id
                    ).padStart(
                      4,
                      "0"
                    )}`}
                  </td>

                  <td>
                    <div className="modern-customer-name-wrap">

                      <div className="modern-customer-avatar">
                        {c.name?.charAt(
                          0
                        )}
                      </div>

                      <span>
                        {c.name}
                      </span>

                    </div>
                  </td>

                  <td>
                    {c.phone || "-"}
                  </td>

                  <td>
                    {c.address || "-"}
                  </td>

                  <td>

                    <button
                      className={`modern-status-badge ${
                        c.status ===
                        "active"
                          ? "active"
                          : "inactive"
                      }`}
                      onClick={() =>
                        toggleStatus(
                          c.id,
                          c.status
                        )
                      }
                    >

                      {c.status ===
                      "active"
                        ? "Active"
                        : "Inactive"}

                    </button>

                  </td>

                  <td>

                    <span className="modern-customer-type">

                      {c.customer_type
                        ?.charAt(0)
                        .toUpperCase() +
                        c.customer_type?.slice(
                          1
                        )}

                    </span>

                  </td>

                  <td>

                    <div className="modern-customer-actions">

                      <button
                        className="modern-edit-btn"
                        onClick={() =>
                          handleRowClick(
                            c
                          )
                        }
                      >
                        Edit
                      </button>

                      <button
                        className="modern-delete-btn"
                        onClick={() =>
                          handleDeleteCustomer(
                            c.id
                          )
                        }
                      >
                        Delete
                      </button>

                    </div>

                  </td>

                </tr>
              ))
            )}

          </tbody>

        </table>


<div className="modern-customers-pagination">

  <button
    disabled={currentPage === 1}
    onClick={() =>
      setCurrentPage((p) =>
        Math.max(p - 1, 1)
      )
    }
  >
    Previous
  </button>

  <span>
    Page {currentPage} of {totalPages || 1}
  </span>

  <button
    disabled={
      currentPage === totalPages ||
      totalPages === 0
    }
    onClick={() =>
      setCurrentPage((p) =>
        Math.min(
          p + 1,
          totalPages
        )
      )
    }
  >
    Next
  </button>

</div>


      </div>

    </div>

{/* =====================================================
    CUSTOMER MODAL
===================================================== */}

{(selectedCustomer || showAddModal) && (

  <div className="modern-modal-overlay">

    <div className="modern-customer-split-modal">

      {/* =========================================
          LEFT PANEL
      ========================================== */}
      <div className="modern-customer-modal-left">

        <div className="modern-customer-modal-pill">
          Customer Profile
        </div>

        <h2>
          {selectedCustomer
            ? "Edit Customer"
            : "Add Customer"}
        </h2>

        <p>
          {selectedCustomer
            ? "Update customer information, loyalty details and POS profile."
            : "Create a new customer profile for transactions, loyalty and receivables integration."}
        </p>

        {/* LIVE CARD */}
        <div className="modern-customer-preview-card">

          <div className="modern-preview-avatar">

            {(
              selectedCustomer?.name ||
              newCustomer?.name ||
              "C"
            )
              .charAt(0)
              .toUpperCase()}

          </div>

          <div className="modern-preview-details">

            <h3>
              {selectedCustomer?.name ||
                newCustomer?.name ||
                "Customer Name"}
            </h3>

            <span>
              {selectedCustomer?.phone ||
                newCustomer?.phone ||
                "No Phone Added"}
            </span>

            <p>
              {selectedCustomer?.address ||
                newCustomer?.address ||
                "No Address Added"}
            </p>

          </div>

        </div>

        {/* LIVE STATUS */}
        <div className="modern-preview-stats">

          <div className="modern-preview-stat">

            <label>
              Status
            </label>

            <strong>
              {selectedCustomer?.status ||
                newCustomer?.status ||
                "active"}
            </strong>

          </div>

          <div className="modern-preview-stat">

            <label>
              Type
            </label>

            <strong>
              {selectedCustomer?.customer_type ||
                newCustomer?.customer_type ||
                "customer"}
            </strong>

          </div>

        </div>

      </div>

      {/* =========================================
          RIGHT PANEL
      ========================================== */}
      <div className="modern-customer-modal-right">

        <div className="modern-customer-modal-header">

          <h3>
            {selectedCustomer
              ? "Customer Information"
              : "New Customer"}
          </h3>

          <button
            className="modern-close-modal-btn"
            onClick={() => {
              setSelectedCustomer(null);
              setShowAddModal(false);
            }}
          >
            ×
          </button>

        </div>

        <div className="modern-customer-form-grid">

          {/* CUSTOMER NO */}
          <div className="modern-form-group">

            <label>
              Customer No.
            </label>

            <input
              disabled
              value={
                selectedCustomer
                  ? `RCS${String(
                      selectedCustomer.id
                    ).padStart(4, "0")}`
                  : `RCS${String(
                      nextCustomerId
                    ).padStart(4, "0")}`
              }
            />

          </div>

          {/* NAME */}
          <div className="modern-form-group">

            <label>
              Name
            </label>

            <input
              type="text"
              value={
                selectedCustomer
                  ? selectedCustomer.name
                  : newCustomer.name
              }
              onChange={(e) => {

                if (selectedCustomer) {

                  handleChange(
                    "name",
                    e.target.value
                  );

                } else {

                  setNewCustomer({
                    ...newCustomer,
                    name: e.target.value,
                  });

                }

              }}
            />

          </div>

          {/* PHONE */}
          <div className="modern-form-group full">

            <label>
              Phone Number
            </label>

            <div className="modern-phone-row">

              <select
                value={
                  selectedCustomer
                    ? selectedCustomer.countryCode || "+592"
                    : newCustomer.countryCode || "+592"
                }
                onChange={(e) => {

                  if (selectedCustomer) {

                    handleChange(
                      "countryCode",
                      e.target.value
                    );

                  } else {

                    setNewCustomer({
                      ...newCustomer,
                      countryCode: e.target.value,
                    });

                  }

                }}
              >

                <option value="+592">
                  +592
                </option>

                <option value="+1">
                  +1
                </option>

                <option value="+44">
                  +44
                </option>

                <option value="+91">
                  +91
                </option>

              </select>

              <input
                type="text"
                value={
                  selectedCustomer
                    ? selectedCustomer.phone || ""
                    : newCustomer.phone
                }
                onChange={(e) => {

                  if (selectedCustomer) {

                    handleChange(
                      "phone",
                      e.target.value
                    );

                  } else {

                    setNewCustomer({
                      ...newCustomer,
                      phone: e.target.value,
                    });

                  }

                }}
              />

            </div>

          </div>

          {/* ADDRESS */}
          <div className="modern-form-group full">

            <label>
              Address
            </label>

            <input
              type="text"
              value={
                selectedCustomer
                  ? selectedCustomer.address
                  : newCustomer.address
              }
              onChange={(e) => {

                if (selectedCustomer) {

                  handleChange(
                    "address",
                    e.target.value
                  );

                } else {

                  setNewCustomer({
                    ...newCustomer,
                    address: e.target.value,
                  });

                }

              }}
            />

          </div>

          {/* STATUS */}
          <div className="modern-form-group">

            <label>
              Status
            </label>

            <select
              value={
                selectedCustomer
                  ? selectedCustomer.status
                  : newCustomer.status
              }
              onChange={(e) => {

                if (selectedCustomer) {

                  handleChange(
                    "status",
                    e.target.value
                  );

                } else {

                  setNewCustomer({
                    ...newCustomer,
                    status: e.target.value,
                  });

                }

              }}
            >

              <option value="active">
                Active
              </option>

              <option value="inactive">
                Inactive
              </option>

            </select>

          </div>

          {/* TYPE */}
          <div className="modern-form-group">

            <label>
              Customer Type
            </label>

            <select
              value={
                selectedCustomer
                  ? selectedCustomer.customer_type
                  : newCustomer.customer_type || "customer"
              }
              onChange={(e) => {

                if (selectedCustomer) {

                  handleChange(
                    "customer_type",
                    e.target.value
                  );

                } else {

                  setNewCustomer({
                    ...newCustomer,
                    customer_type: e.target.value,
                  });

                }

              }}
            >

              <option value="customer">
                Customer
              </option>

              <option value="vendor">
                Vendor
              </option>

              <option value="agent">
                Agent
              </option>

            </select>

          </div>

        </div>

        {/* ACTIONS */}
        <div className="modern-modal-actions">

          <button
            className="modern-save-btn"
            onClick={
              selectedCustomer
                ? handleSave
                : handleAddCustomer
            }
          >
            {selectedCustomer
              ? "Save Changes"
              : "Add Customer"}
          </button>

          <button
            className="modern-cancel-btn"
            onClick={() => {
              setSelectedCustomer(null);
              setShowAddModal(false);
            }}
          >
            Cancel
          </button>

        </div>

      </div>

    </div>

  </div>
)}


{/* =========================================
    PROCESSING MODAL
========================================= */}

{processing && (

  <div className="modern-processing-overlay">

    <div className="modern-processing-modal">

      {/* LOADING */}
      {!processStatus && (

        <>

          <div className="accounts-loader-container">

            <div className="accounts-loader-glow" />

            <div className="accounts-loader-spinner">

              <div className="accounts-loader-ring" />

              <div className="accounts-loader-ring-secondary" />

              <div className="accounts-loader-logo">

                <img
                  src={rcsLogo}
                  alt="RCS Logo"
                />

              </div>

            </div>

          </div>

          <div className="modern-processing-text">

            <h2>
              Processing Request
            </h2>

            <p>
              {processMessage}
            </p>

          </div>

        </>

      )}

      {/* SUCCESS */}
      {processStatus === "success" && (

        <div className="modern-process-result success">

          <div className="modern-process-icon success">

            ✓

          </div>

          <h2>
            Success
          </h2>

          <p>
            {processMessage}
          </p>

        </div>

      )}

      {/* ERROR */}
      {processStatus === "error" && (

        <div className="modern-process-result error">

          <div className="modern-process-icon error">

            ✕

          </div>

          <h2>
            Error
          </h2>

          <p>
            {processMessage}
          </p>

        </div>

      )}

    </div>

  </div>
)}

  </div>
);
};

export default Customers;
